/**
 * Vite Environment Variables Accessor
 *
 * This file is processed by Vite's define plugin which replaces placeholders
 * at build time. TypeScript only type-checks this, but Vite handles the actual replacement.
 */

declare global {
    /** Injected by Vite `define` in vite.config.ts (build-time string, may be `""`). */
    var __VITE_HUBSPOT_IDENTIFY_PAYLOAD_SECRET__: string;
    var __VITE_HUBSPOT_PORTAL_ID__: string;
    var __VITE_HUBSPOT_SCRIPT_HOST__: string;
}

/**
 * Get CPQ MFE URL from environment
 *
 * NOTE: Vite will replace __VITE_CPQ_MFE_URL__ with the actual environment variable value
 * during the build process. This placeholder pattern avoids import.meta which TypeScript
 * in CommonJS mode doesn't support.
 *
 * @returns The CPQ MFE URL if configured, undefined otherwise
 */
export const getCpqMfeUrl = (): string | undefined => {
    // This will be replaced by Vite's define plugin at build time
    // @ts-expect-error - Vite define plugin replaces this global at build time
    return typeof __VITE_CPQ_MFE_URL__ !== "undefined" ? __VITE_CPQ_MFE_URL__ : undefined;
};

/**
 * Get the CPQ base URL (origin + /cds/cpq) derived from the MFE URL.
 *
 * @returns The CPQ base URL if configured, undefined otherwise
 */
export const getCpqBaseUrl = (): string | undefined => {
    const mfeUrl = getCpqMfeUrl();
    if (!mfeUrl) {
        return undefined;
    }
    try {
        const parsed = new URL(mfeUrl);
        if (parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1") {
            // Use relative path during local dev — Vite proxy handles routing
            return "/cds/cpq";
        }
        // In production, use the full origin URL so requests go to the CPQ server
        return `${parsed.origin}/cds/cpq`;
    } catch {
        // Already a relative URL — keep relative path
        return "/cds/cpq";
    }
};

/**
 * Get App-Base MFE URL from environment
 *
 * @returns The App-Base MFE URL if configured, undefined otherwise
 */
export const getAppBaseMfeUrl = (): string | undefined => {
    // @ts-expect-error - Vite define plugin replaces this global at build time
    return typeof __VITE_APP_BASE_MFE_URL__ !== "undefined" ? __VITE_APP_BASE_MFE_URL__ : undefined;
};

/**
 * Shared secret for decrypting HubSpot identify API payloads (`{ v: 1, d }`).
 * Same pattern as getCpqMfeUrl — avoids `import.meta` (website tsconfig uses `module: CommonJS`).
 */
export const getHubspotIdentifyPayloadSecret = (): string | undefined => {
    const t = __VITE_HUBSPOT_IDENTIFY_PAYLOAD_SECRET__.trim();
    return t.length > 0 ? t : undefined;
};
